import type { FeaturedProductsConfigDTO } from '../featured-products/featured-products.types'
import type { SmartImageAssetDTO } from './asset.types'
// Enums
export enum BillboardVariantEnum {
  MULTI = 'multi',
  SMALL = 'small',
  LARGE = 'large',
  MEDIUM = 'medium',
}

export type ImagePosition = 'left' | 'right' | undefined
export interface ContentEntryTrackingMetaDTO {
  isSegmented?: boolean
  segments?: string[]
}
// Components
export interface BillboardDTO extends ContentEntryTrackingMetaDTO {
  id: string
  type: string
  internalName: string
  referenceKey: string
  variant?: BillboardVariantEnum
  contents: BillboardContentDTO[]
}

export interface BillboardContentDTO extends ContentEntryTrackingMetaDTO {
  id: string
  internalName: string
  ctaText: string
  tnc?: {
    label: string
    content: string
  }
  banner: {
    title: string
    subtitle: string
    urlPath: string
    textColor: string
    backgroundColor: string
    image: SmartImageAssetDTO
  }
  isOpenInNewTab: boolean
}

export interface BrandBannerDTO extends ContentEntryTrackingMetaDTO {
  id: string
  type: string
  internalName: string
  referenceKey: 'BrandBanner'
  title: string
  subtitle: string
  brands: BrandBannerTileDTO[]
}

export interface BrandPageBannerDTO extends ContentEntryTrackingMetaDTO {
  id: string
  type: string
  internalName: string
  referenceKey: 'BrandPageBanner'
  brandLogo: SmartImageAssetDTO
  brandName: string
  description: string
  tags?: string[]
  billboard?: BillboardDTO
}

export interface BrandBannerTileDTO extends ContentEntryTrackingMetaDTO {
  id: string
  internalName: string
  image: SmartImageAssetDTO
  url: string
  isOpenInNewTab: boolean
}

export interface CampaignQuickLinkDTO extends ContentEntryTrackingMetaDTO {
  id: string
  type: string
  referenceKey: 'CampaignQuickLink'
  title: string
  internalName: string
  campaignCards: CampaignCardDTO[]
}

export interface CampaignCardDTO extends ContentEntryTrackingMetaDTO {
  id: string
  image: SmartImageAssetDTO
  title: string
  subtitle: string
  linkUrl: string
  ctaText: string
  tnc?: {
    label: string
    content: string
  }
  internalName: string
  isOpenInNewTab: boolean
}

export interface CategoryBannerDTO extends ContentEntryTrackingMetaDTO {
  id: string
  type: string
  referenceKey: 'CategoryBanner'
  title: string
  subtitle: string
  categories: CategoryTileDTO[]
  internalName: string
}

export interface CategoryCardDTO extends ContentEntryTrackingMetaDTO {
  id: string
  internalName: string
  image: SmartImageAssetDTO
  label: string
  urlPath: string
  openInNewTab: boolean
}

export interface CategoryQuickLinkDTO extends ContentEntryTrackingMetaDTO {
  id: string
  type: string
  internalName: string
  referenceKey: string
  filterType: string
  title: string
  subTitle: string
  categoryCards: CategoryCardDTO[]
}

export interface CategoryTileDTO extends ContentEntryTrackingMetaDTO {
  id: string
  internalName: string
  title: string
  url: string
  isOpenInNewTab: boolean
  image: SmartImageAssetDTO
}

export interface ContentBannerDTO extends ContentEntryTrackingMetaDTO {
  id: string
  type: string
  internalName: string
  referenceKey: string
  contentCards: ContentCardDTO[]
}

export interface ContentCardDTO extends ContentEntryTrackingMetaDTO {
  id: string
  internalName: string
  image: SmartImageAssetDTO
  title: string
  subtitle: string
  ctaText?: string
  linkUrl: string
  isOpenInNewTab: boolean
}
export interface FeatureBannerDTO extends ContentEntryTrackingMetaDTO {
  id: string
  type: string
  internalName: string
  referenceKey: 'FeatureBanner'
  title: string
  subtitle?: string
  ctaText?: string
  urlPath: string
  image: SmartImageAssetDTO
  textColor?: string
  backgroundColor?: string
  isOpenInNewTab?: boolean
}

export interface FeaturedProductsDTO extends ContentEntryTrackingMetaDTO {
  id: string
  type: string
  internalName: string
  referenceKey: string
  title: string
  subtitle?: string
  ctaLabel?: string
  urlPath?: string
  isOpenInNewTab: boolean
  showAddToBag: boolean
  productConfig: FeaturedProductsConfigDTO
}

export interface IconLinkDTO {
  id: string
  type: string
  internalName: string
  referenceKey: string
  icon: SmartImageAssetDTO
  altText: string
  urlPath: string
  isOpenInNewTab: boolean
}

export interface ImageBannerDTO extends ContentEntryTrackingMetaDTO {
  id: string
  type: string
  internalName: string
  referenceKey: string
  banner: SmartImageAssetDTO
  altText: string
  urlPath?: string
  isOpenInNewTab: boolean
}

export interface ImageTextBannerDTO extends ContentEntryTrackingMetaDTO {
  id: string
  type: string
  internalName: string
  referenceKey: string
  image: SmartImageAssetDTO
  imagePosition: ImagePosition
  title: string
  subTitle?: string
  backgroundColor: string
  ctaText?: string
  ctaUrl?: string
  openInNewTab: boolean
}

export interface LinkDTO {
  id: string
  type: string
  internalName: string
  referenceKey: string
  title: string
  urlPath: string
  isOpenInNewTab: boolean
}

export interface OverlayDTO extends ContentEntryTrackingMetaDTO {
  id: string
  internalName: string
  platform: string[]
  isOpenInNewTab: boolean
  image: SmartImageAssetDTO
  imageAltText: string
  urlPath: string
  includedPages: string[]
  excludedPages: string[]
}

export interface PaymentMethodDTO {
  id: string
  type: string
  internalName: string
  referenceKey: string
  name: string
  icon: SmartImageAssetDTO
}

export interface ProductListingBannerDTO extends ContentEntryTrackingMetaDTO {
  id: string
  type: string
  internalName: string
  referenceKey: string
  banner: SmartImageAssetDTO
  altText: string
  urlPath?: string
  isOpenInNewTab: boolean
}

export interface ProductListingContainerDTO
  extends ContentEntryTrackingMetaDTO {
  id: string
  type: string
  internalName: string
  referenceKey: string
  productGroupSlugUrl?: string
  initialBannerPosition: number
  bannerOffsetCount: number
  productListingBanners?: ProductListingBannerDTO[]
}

export interface PromotionBannerDTO {
  id: string
  type: string
  internalName: string
  referenceKey: string
  title: string
  subtitle?: string
  urlPath?: string
  platform: string
  isOpenInNewTab: boolean
}

export interface SeoFooterDTO extends ContentEntryTrackingMetaDTO {
  id: string
  type: string
  referenceKey: string
  content: string
}

export interface SiteWideBannerDTO extends ContentEntryTrackingMetaDTO {
  id: string
  type: string
  internalName: string
  referenceKey: string
  backgroundColor: string
  content: string
  isOpenInNewTab: boolean
  textColor: string
  urlPath: string
}
